import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { EMPTY, Observable, catchError, finalize, switchMap, take, tap } from 'rxjs';
import { patch } from '@ngxs/store/operators';
import { AuthorizationData } from '@kaseya-shared';
import { LoginAttemptService } from '@core/authorization/services/login-attempt.service';
import { LoginAttempt } from '@core/authorization/models/login-attempt.interface';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import {
  MfaTokenExpiredError,
  VerifyTotpMfa,
  VerifyTotpMfaCompleted,
  VerifyTotpMfaError,
  VerifyTotpMfaInvalidTotpCodeError,
  VerifyTotpMfaUserLockedOutError,
  VerifyTotpMfaUserNotFoundError,
} from '@pages/totp-mfa-verification/store/actions/totp-mfa-verification.actions';
import { TotpMfaVerificationService } from '@pages/totp-mfa-verification/services/totp-mfa-verification.service';

interface TotpMfaVerificationStateModel {
  loading: boolean;
}

@State<TotpMfaVerificationStateModel>({
  name: 'totpMfaVerification',
  defaults: {
    loading: false,
  },
})
@Injectable()
export class TotpMfaVerificationState {
  private readonly totpMfaVerificationService = inject(TotpMfaVerificationService);
  private readonly loginAttemptService = inject(LoginAttemptService);

  @Selector()
  static loading(state: TotpMfaVerificationStateModel): boolean {
    return state.loading;
  }

  @Action(VerifyTotpMfa)
  verifyTotpMfa(context: StateContext<TotpMfaVerificationStateModel>, { passCode }: VerifyTotpMfa): Observable<AuthorizationData> {
    context.setState(patch<TotpMfaVerificationStateModel>({ loading: true }));

    return this.loginAttemptService.onLoginAttempt.pipe(
      take(1),
      switchMap(({ mfaToken }: LoginAttempt) =>
        this.totpMfaVerificationService.verifyTotpMfa({ passCode }, mfaToken).pipe(
          finalize(() => context.setState(patch<TotpMfaVerificationStateModel>({ loading: false }))),
          tap(authorizationData => context.dispatch(new VerifyTotpMfaCompleted(authorizationData))),
          catchError((errorResponse: HttpErrorResponse) => {
            if (errorResponse.status === HttpStatusCode.BadRequest) {
              if (errorResponse.error === 'UserNotFound') {
                context.dispatch(new VerifyTotpMfaUserNotFoundError());
                return EMPTY;
              }

              if (errorResponse.error === 'InvalidTotpCode') {
                context.dispatch(new VerifyTotpMfaInvalidTotpCodeError());
                return EMPTY;
              }

              if (errorResponse.error === 'UserLockedOut') {
                context.dispatch(new VerifyTotpMfaUserLockedOutError());
                return EMPTY;
              }
            }

            if (errorResponse.status === HttpStatusCode.Unauthorized) {
              context.dispatch(new MfaTokenExpiredError());
              return EMPTY;
            }

            context.dispatch(new VerifyTotpMfaError());
            return EMPTY;
          })
        )
      )
    );
  }
}
